import React, { ReactNode, FormEvent, useState } from 'react';
import toast from 'react-hot-toast';

interface EmailFormHandlerProps {
  children: ReactNode;
  formName: string;
  className?: string;
  onSubmit?: (e: FormEvent) => void;
}

/**
 * A wrapper component that adds email functionality to any form
 * Sends form data via Supabase Edge Function which:
 * 1. Stores submissions in database
 * 2. Sends emails to hello@proxyemobility.com via Resend
 */
const EmailFormHandler: React.FC<EmailFormHandlerProps> = ({
  children,
  formName,
  className,
  onSubmit
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    try {
      const data: Record<string, any> = {};
      formData.forEach((value, key) => {
        if (!key.startsWith('_')) {
          data[key] = value.toString();
        }
      });

      const customerEmail = data.email || data.customer_email || data.contact_email;
      const customerName = data.name || data.full_name || data.first_name ||
                          `${data.first_name || ''} ${data.last_name || ''}`.trim() ||
                          data.company_name;

      const formType = getFormType(formName);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Configuration manquante');
      }

      const functionUrl = `${supabaseUrl}/functions/v1/send-form-email`;

      console.log('Envoi du formulaire vers:', functionUrl);

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify({
          formType,
          formData: data,
          customerEmail,
          customerName,
          ipAddress: typeof window !== 'undefined' ? window.location.hostname : undefined,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        }),
      });

      const result = await response.json();
      console.log('Résultat de l\'envoi:', result);

      if (response.ok && result.success) {
        toast.success('Votre demande a été envoyée avec succès! Nous vous contacterons bientôt.');
        form.reset();

        if (onSubmit) {
          onSubmit(e);
        }
      } else {
        throw new Error(result.error || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du formulaire:', error);

      toast.error(
        'Erreur technique. Veuillez réessayer ou contacter hello@proxyemobility.com',
        { duration: 8000 }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={className}
      noValidate
    >
      {children}
    </form>
  );
};

function getFormType(formName: string): string {
  const lowerName = formName.toLowerCase();

  if (lowerName.includes('contact')) return 'contact';
  if (lowerName.includes('précommande') || lowerName.includes('preorder')) return 'preorder';
  if (lowerName.includes('pay molo') || lowerName.includes('paymolo')) return 'paymolo';
  if (lowerName.includes('simulation')) return 'simulation';
  if (lowerName.includes('essai') || lowerName.includes('trial')) return 'trial';
  if (lowerName.includes('réservation') || lowerName.includes('reservation')) return 'reservation';
  if (lowerName.includes('formation') || lowerName.includes('training')) return 'training';

  return 'contact';
}

export default EmailFormHandler;
